import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import OrderTransfer from "./OrderTransfer";
import Footer from "../../components/Footer";
import SideBar from "../../components/SideBar";
import Header from "../../components/Header";
import AddIcon from "@mui/icons-material/Add";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import TransferWithinAStationIcon from "@mui/icons-material/TransferWithinAStation";
import OrderStatus from "./OrderStatus";
import OrderConfirm from "./OrderConfirm";
import MakeReceipt from "./MakeReceipt";

import { useUserContext } from "../../context/UserContext";

function OrderPage() {
  const { slug } = useParams();
  const { user } = useUserContext();
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeAction, setActiveAction] = useState("addItem");

  const [tableId, setTableId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const totalItemCount = orderItems.length;

  const localStorageKey = tableId ? `orderItems_${tableId}` : null;

  const totalConfirmItemCount = useMemo(() => {
    return orders.reduce((total, order) => {
      if (
        order.order_status === "confirmed" &&
        order.table_id === tableId &&
        Array.isArray(order.order_items)
      ) {
        return total + order.order_items.length;
      }
      return total;
    }, 0);
  }, [orders, tableId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tableRes = await axios.get(
          `${API_BASE_URL}/tables/get_table_id_by_slug?slug=${slug}`
        );
        const tableData = tableRes?.data?.item;
        if (!tableData) return setError("Table not found.");
        setTableId(tableData.id);

        const [itemRes, catRes, orderRes, orderIdRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/Menu_category/get_all_item`),
          axios.get(`${API_BASE_URL}/Menu_category/all`),
          axios.get(`${API_BASE_URL}/orders/get_order_by_table_slug/${slug}`),
          axios.get(`${API_BASE_URL}/orders/get_order_id_by_slug/${slug}`),
        ]);

        setItems(itemRes?.data?.items || []);
        setCategories(catRes?.data?.items || []);
        setOrders(orderRes?.data?.data || []);
        setOrderId(parseInt(orderIdRes?.data?.orderId || 0));
      } catch (err) {
        setError("Failed to load data.");
        console.error(err);
      }
    };

    fetchData();
  }, [slug]);

  useEffect(() => {
    if (localStorageKey) {
      const savedItems =
        JSON.parse(localStorage.getItem(localStorageKey)) || [];
      setOrderItems(savedItems);
    }
  }, [localStorageKey]);

  useEffect(() => {
    let timeoutId;

    if (totalItemCount > 0 && totalConfirmItemCount === 0) {
      timeoutId = setTimeout(() => {
        setActiveAction("holdOrder");
      }, 3000); // 3 seconds delay
    } else if (totalConfirmItemCount > 0 && totalItemCount === 0) {
      setActiveAction("viewOrders");
    } else {
      setActiveAction("addItem");
    }

    // Cleanup to prevent memory leaks or multiple triggers
    return () => clearTimeout(timeoutId);
  }, [totalItemCount, totalConfirmItemCount]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const filteredItems = items
    .filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((item) => !selectedCategory || item.category === selectedCategory)
    .slice(0, 10);

  const getCategoryNameById = (id) =>
    categories.find((cat) => cat.id === id)?.category_name || "Unknown";

  const handleAddToOrder = (item) => {
    if (!tableId) return alert("Table ID not available.");

    const index = orderItems.findIndex((i) => i.id === item.id);
    const updated = [...orderItems];

    if (index !== -1) {
      updated[index].quantity += 1;
    } else {
      updated.push({ ...item, quantity: 1 });
    }

    setOrderItems(updated);
    localStorage.setItem(localStorageKey, JSON.stringify(updated));
  };

  const handleQuantityChange = (id, inc) => {
    const updated = orderItems.map((item) =>
      item.id === id
        ? {
            ...item,
            quantity: inc ? item.quantity + 1 : Math.max(1, item.quantity - 1),
          }
        : item
    );
    setOrderItems(updated);
    localStorage.setItem(localStorageKey, JSON.stringify(updated));
  };

  const handleDeleteItem = (id) => {
    const updated = orderItems.filter((item) => item.id !== id);
    setOrderItems(updated);
    localStorage.setItem(localStorageKey, JSON.stringify(updated));
    if (updated.length === 0) localStorage.removeItem(localStorageKey);
  };

  const handleConfirmOrder = async () => {
    if (!tableId) return;

    setIsSubmitting(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/tables/get_table_status_by_id?id=${tableId}`
      );
      const status = res.data?.item?.tables_status;

      const data = {
        tableId,
        order_status: "confirmed",
        orderItems,
        created_by: user,
      };

      if (status !== "available") {
        await updateOrder(orderId, orderItems);
        return;
      }

      const confirmRes = await axios.post(
        `${API_BASE_URL}/orders/createOrder`,
        data
      );

      if (confirmRes.status === 200) {
        toast.success(confirmRes.data.status_message || "Order confirmed!");
        setOrderItems([]);
        localStorage.removeItem(localStorageKey);
        const updatedOrders = await axios.get(
          `${API_BASE_URL}/orders/get_order_by_table_slug/${slug}`
        );
        setOrders(updatedOrders.data.data || []);
        const idRes = await axios.get(
          `${API_BASE_URL}/orders/get_order_id_by_slug/${slug}`
        );
        setOrderId(parseInt(idRes.data.orderId, 10));
      } else {
        toast.error("Order failed.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error confirming order.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateOrder = async (orderId, itemsToAdd) => {
    if (!orderId || !itemsToAdd.length) return;

    try {
      const statusRes = await axios.get(
        `${API_BASE_URL}/orders/get_order_by_id/${orderId}`
      );
      const currentOrder = statusRes?.data?.data;

      if (currentOrder?.order_status !== "confirmed") {
        toast.error("Order is not confirmed yet.");
        return;
      }

      const updateRes = await axios.post(
        `${API_BASE_URL}/orders/addNewItemsToOrder`,
        {
          orderId,
          orderItems: itemsToAdd,
          updated_by: user,
        }
      );

      if (updateRes.status === 200) {
        toast.success("Order updated.");
        setOrderItems([]);
        localStorage.removeItem(localStorageKey);
        const updated = await axios.get(
          `${API_BASE_URL}/orders/get_order_by_table_slug/${slug}`
        );
        setOrders(updated.data.data || []);
      } else {
        toast.error("Update failed.");
      }
    } catch (err) {
      toast.error("Error updating order.");
      console.error(err);
    }
  };

  return (
    <>
      <SideBar />
      <Header />
      <div className="MainContainer">
        {error && <div className="error">{error}</div>}
        <div className="OrderHeadersss">
          <div className="TableNumber">
            <h2>Table :</h2>
            <input type="text" value={slug} readOnly />
          </div>

          <div className="ActionOrder">
            <button
              className={activeAction === "addItem" ? "active" : ""}
              onClick={() => setActiveAction("addItem")}
            >
              <AddIcon /> Add Item
            </button>
            <button
              className={activeAction === "holdOrder" ? "active" : ""}
              onClick={() => setActiveAction("holdOrder")}
            >
              <HourglassEmptyIcon /> Hold Order{" "}
              {totalItemCount > 0 && <span>{totalItemCount}</span>}
            </button>
            <button
              className={activeAction === "viewOrders" ? "active" : ""}
              onClick={() => setActiveAction("viewOrders")}
            >
              <VisibilityIcon /> View Orders{" "}
              {totalConfirmItemCount > 0 && (
                <span>{totalConfirmItemCount}</span>
              )}
            </button>
            <button
              className={activeAction === "makeReceipt" ? "active" : ""}
              onClick={() => setActiveAction("makeReceipt")}
            >
              <PrintIcon /> Make Receipt
            </button>
            <button
              className={activeAction === "orderTransfer" ? "active" : ""}
              onClick={() => setActiveAction("orderTransfer")}
            >
              <TransferWithinAStationIcon /> Order Transfer
            </button>
          </div>
        </div>

        {activeAction === "addItem" && (
          <div className="OrderItemsBoxContainer">
            <div className="CategoriesBox">
              <div className="CategoryList">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={selectedCategory === cat.id ? "active" : ""}
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.category_name}
                  </button>
                ))}
              </div>
            </div>
            <div className="MenuitemsBox">
              <input
                type="text"
                placeholder="Search by item name"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <div className="MenuItemsList">
                {filteredItems.map((item) => (
                  <div className="MenuBox" key={item.id}>
                    <div className="img">
                      <img src={item.docpath} alt={item.name} />
                    </div>
                    <div className="MenuTitle">
                      <h3>{item.name}</h3>
                      <p>{getCategoryNameById(item.category)}</p>
                      <h3>{item.price}</h3>
                    </div>
                    <button
                      className="AddToOrderBtn"
                      onClick={() => handleAddToOrder(item)}
                    >
                      +
                    </button>
                  </div>
                ))}
              </div>
              {filteredItems.length === 0 && <p>No items found.</p>}
            </div>
          </div>
        )}

        {activeAction === "holdOrder" && (
          <OrderStatus
            orderItems={orderItems}
            handleQuantityChange={handleQuantityChange}
            handleDeleteItem={handleDeleteItem}
            handleConfirmOrder={handleConfirmOrder}
            isSubmitting={isSubmitting}
          />
        )}
        {activeAction === "viewOrders" && (
          <OrderConfirm orders={orders} setOrders={setOrders} />
        )}
        {activeAction === "makeReceipt" && (
          <MakeReceipt
            orders={orders}
            orderId={orderId}
            tableId={tableId}
            setOrders={setOrders}
          />
        )}
        {activeAction === "orderTransfer" && (
          <OrderTransfer
            tableId={tableId}
            orderId={orderId}
            setOrders={setOrders}
          />
        )}
      </div>
      <Footer />
    </>
  );
}

export default OrderPage;
